import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, ArrowUpRight } from "lucide-react";
import { SITE, NAV_LINKS, LEGAL_LINKS } from "@/lib/siteConfig";

export default function Footer() {
  return (
    <footer
      className="bg-[#1E2761] text-white pt-20 pb-10 px-6 md:px-10"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-16 border-b border-white/10">
          {/* Brand block */}
          <div className="md:col-span-5">
            <div className="font-display text-2xl md:text-3xl tracking-tight mb-4 text-white whitespace-nowrap">
              NextChapter<span className="text-[#C9A84C]">4</span>You
            </div>
            <p className="text-white/70 text-base leading-relaxed max-w-md">
              Human-First Coaching. Genuine Career Support. Executive
              coaching and expat career transition support, based in the
              Netherlands and working across Europe.
            </p>
            <div className="mt-8 flex items-center gap-3">
              <a
                href={SITE.linkedinBrand}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin"
                className="w-10 h-10 border border-white/20 hover:border-[#C9A84C] hover:text-[#C9A84C] flex items-center justify-center transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin size={16} />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.18em] text-[#C9A84C] mb-5">
              Navigate
            </div>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.path}>
                  <Link
                    to={l.path}
                    data-testid={`footer-nav-${l.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-white/80 hover:text-[#C9A84C] transition-colors text-sm"
                  >
                    {l.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.18em] text-[#C9A84C] mb-5">
              Get in Touch
            </div>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Mail size={16} className="mt-0.5 text-[#C9A84C] flex-shrink-0" />
                <a
                  href={`mailto:${SITE.email}`}
                  data-testid="footer-email"
                  className="text-white/85 hover:text-white"
                >
                  {SITE.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={16} className="mt-0.5 text-[#C9A84C] flex-shrink-0" />
                <a
                  href={`tel:${SITE.phoneRaw}`}
                  data-testid="footer-phone"
                  className="text-white/85 hover:text-white"
                >
                  {SITE.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={16} className="mt-0.5 text-[#C9A84C] flex-shrink-0" />
                <span className="text-white/85">{SITE.address}</span>
              </li>
            </ul>
            <Link
              to="/contact"
              data-testid="footer-cta-contact"
              className="mt-6 inline-flex items-center gap-2 text-sm text-white border-b border-[#C9A84C] pb-1 hover:gap-3 transition-all"
            >
              Start the conversation <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/60">
          <div className="flex flex-col md:flex-row md:items-center gap-x-4 gap-y-1">
            <span>© {SITE.copyrightYear} NextChapter4You.nl. All rights reserved.</span>
            <span className="hidden md:inline text-white/30">|</span>
            <span>KvK {SITE.kvk}</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {LEGAL_LINKS.map((l) => (
              <Link
                key={l.path}
                to={l.path}
                data-testid={`footer-legal-${l.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="hover:text-[#C9A84C] transition-colors"
              >
                {l.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
